import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import TurnstileWidget from '../components/TurnstileWidget';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_AGENCY;

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    agencyName: '',
    dbaName: '',
    contactName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  // Session 213.63 — Turnstile token + support-email fallback
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  // Honeypot — real users never fill this; bots usually do.
  const [hpWebsite, setHpWebsite] = useState('');

  // Fetch the configured public-form support email so the fail-closed
  // message has a place to send blocked signups.
  useEffect(() => {
    axios.get(`${API_URL}/api/public/captcha/support-email`)
      .then((r) => setSupportEmail(r.data?.support_email || ''))
      .catch(() => { /* leave blank */ });
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the verification check before submitting.');
      setLoading(false);
      return;
    }

    const result = await register(
      formData.email,
      formData.password,
      formData.contactName,
      formData.agencyName,
      formData.phoneNumber,
      formData.dbaName,
      { turnstile_token: turnstileToken, website: hpWebsite },
    );

    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <Card className="shadow-xl">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2">Agency Appointment Request Submitted!</h2>
              <p className="text-gray-600 mb-6">
                Your agency appointment request has been submitted successfully. A Zion Underwriting administrator will review your application, send you the producer survey and agreement, and contact you shortly to finalize your agency setup.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/assets/zion-logo.jpeg"
            alt="Zion Underwriting Partners"
            className="h-20 w-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Agency Appointment Request</h1>
          <p className="text-gray-600 mt-2">Apply to become an appointed agency</p>
        </div>

        <Card className="shadow-xl">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div>
                <Label htmlFor="agencyName">Agency Name *</Label>
                <Input
                  type="text"
                  id="agencyName"
                  name="agencyName"
                  value={formData.agencyName}
                  onChange={handleChange}
                  placeholder="ABC Insurance Agency"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="dbaName">
                  DBA Name <span className="text-gray-400 text-xs">(optional)</span>
                </Label>
                <Input
                  type="text"
                  id="dbaName"
                  name="dbaName"
                  value={formData.dbaName}
                  onChange={handleChange}
                  placeholder="Doing Business As (trade name)"
                  className="mt-2"
                  data-testid="register-dba-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  If your agency operates under a trade name different from the legal
                  name above, enter it here.
                </p>
              </div>

              <div>
                <Label htmlFor="contactName">Contact Name *</Label>
                <Input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@agency.com"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <Input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                  required
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  required
                  className="mt-2"
                />
              </div>

              {/* Honeypot — visually hidden, no autocomplete, real users never see/fill */}
              <input
                type="text"
                name="hp_url"
                tabIndex={-1}
                autoComplete="off"
                value={hpWebsite}
                onChange={(e) => setHpWebsite(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                aria-hidden="true"
              />

              {/* Cloudflare Turnstile — invisible to most legit users */}
              <div className="pt-2">
                <TurnstileWidget
                  siteKey={TURNSTILE_SITE_KEY}
                  action="agency_signup"
                  onVerify={(token) => { setTurnstileToken(token); setTurnstileError(false); }}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileError(true)}
                />
                {turnstileError && (
                  <div className="mt-2 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">
                    We couldn&apos;t reach our verification service right now.{' '}
                    {supportEmail ? (
                      <>Please email <a className="underline font-semibold" href={`mailto:${supportEmail}?subject=Manual agency signup`}>{supportEmail}</a> to sign up manually.</>
                    ) : (
                      <>Please contact us to sign up manually.</>
                    )}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !turnstileToken}
                className="w-full bg-gradient-to-r from-[#9CA3AF] to-[#6B7280] hover:from-[#6B7280] hover:to-[#4B5563] text-white"
                data-testid="register-submit-btn"
              >
                {loading ? 'Registering...' : 'Register'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#1e3a5f] hover:text-[#6B7280] font-medium">
                  Sign In
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/"
                className="text-sm text-gray-600 hover:text-[#6B7280] inline-flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Home
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
